/* T116 — New Project intake form (Sprint 5 / FR-001).
 *
 * Mirrors `New UI Changes/new-project.html`. POSTs to /api/v1/projects
 * via `createProject` from services/api.ts and routes the analyst to
 * the new Project Workspace on success.
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { createProject } from '../services/api';
import type { ProjectCreateRequest } from '../types/api';

// Sprint 5 — INVESTMENT_TYPES list retired from intake. DD's
// extraction populates Project.investment_type post-analysis.
//
// Sprint 5 client feedback 2026-06-24 — SECTORS list also retired.
// DD's sub_sector / asset_class extractors pull the IC memo's
// explicit "Sector" row (e.g. "Green Hydrogen — Electrolyser
// Industry"). jurisdiction_runner auto-corrects project.sector
// to the DD-extracted value mid-flight.

export function NewProjectPage(): JSX.Element {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState<ProjectCreateRequest>({
    project_name: '',
    target_entity: '',
    lead_analyst_id: user?.id ?? '',
    // Sprint 5 — investment_type + jurisdiction + sector are no
    // longer captured at intake. DD extracts all three from the
    // Investment Memo + IC Resolution. The columns remain on the
    // Project model so DD can backfill them post-analysis. Empty
    // string here means "to be discovered" — the FE displays "—"
    // until DD writes back the extracted value.
    //
    // Sprint 5 client feedback 2026-06-24 (2nd pass) —
    // target_entity + deal_size_usd_m ALSO retired from intake.
    // target_entity remains in form state (BE schema requires
    // non-empty string) but defaults to a placeholder that
    // jurisdiction_runner will overwrite with the DD-extracted
    // target_company_name on first DD run.
    sector: '',
    deal_size_usd_m: null,
    target_close: '',
    notes: '',
  });

  function update<K extends keyof ProjectCreateRequest>(
    key: K,
    value: ProjectCreateRequest[K],
  ): void {
    setForm((f) => ({ ...f, [key]: value }));
  }

  async function handleSubmit(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    setError(null);

    if (!form.project_name.trim()) {
      setError('Please fill in the project name before continuing.');
      return;
    }
    if (!form.lead_analyst_id) {
      setError('Lead analyst is required.');
      return;
    }

    // Sprint 5 client feedback 2026-06-24 (2nd pass) — target_entity
    // input removed from the form; BE schema still requires
    // min_length=1 (Pydantic ProjectCreateRequest.target_entity).
    // Build the POST payload from the form state plus a placeholder
    // target_entity when the user didn't type one — DD's
    // jurisdiction_runner write-back will overwrite this with the
    // extracted target_company_name on first DD completion. Using a
    // separate payload object (NOT a state mutation) keeps React's
    // reconciliation contract intact — `form` stays read-only here.
    const payload: ProjectCreateRequest = {
      ...form,
      target_entity: form.target_entity.trim() || 'To be discovered (DD will populate)',
    };

    try {
      setSubmitting(true);
      const project = await createProject(payload);
      navigate(`/projects/${project.audit_id}`);
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : 'Could not create project. Please try again.',
      );
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="page-body">
      <div className="page-topbar">
        <div>
          <div className="breadcrumb">
            <a href="/dashboard">Dashboard</a>
            <span className="bc-sep">/</span>
            <a href="/projects">Audit AI</a>
            <span className="bc-sep">/</span>
            <span>New Project</span>
          </div>
          <h1 className="page-topbar-title">New Project</h1>
          <p className="page-topbar-sub">
            Set up the project, then begin the Due Diligence Assessment.
          </p>
        </div>
        <a href="/projects" className="btn btn-secondary">
          Cancel
        </a>
      </div>

      {/* Sprint 5 client feedback 2026-06-22 — New Project page
          restyle: modern horizontal stepper per design audit doc
          item #4. Replaces the per-step rectangular tab containers
          with circular numbered indicators connected by thin lines.
          Active step gets the accent (orange) fill; upcoming steps
          are neutral gray. */}
      <ol className="np-stepper" aria-label="Project setup steps">
        {[
          { n: 1, label: 'Project details', active: true },
          { n: 2, label: 'Due Diligence' },
          { n: 3, label: 'SPA Legal Review' },
          { n: 4, label: 'Risk Assessment' },
        ].map((s, idx, arr) => (
          <li
            key={s.n}
            className={`np-stepper-step${s.active ? ' is-active' : ''}`}
          >
            <span className="np-stepper-bubble" aria-hidden="true">
              {s.n}
            </span>
            <span className="np-stepper-label">{s.label}</span>
            {idx < arr.length - 1 && (
              <span className="np-stepper-line" aria-hidden="true" />
            )}
          </li>
        ))}
      </ol>

      {/* Sprint 5 UI-SP3-5 — centered card wrapper. The card itself
          carries the form; the surrounding constraint forces a sane
          max-width so the form doesn't stretch full-width on big
          monitors. */}
      <div
        className="card np-card"
        style={{
          maxWidth: 880,
          margin: '0 auto',
          padding: 'var(--s6)',
        }}
      >
        {/* Sprint 5 client feedback 2026-06-22 — card head restyle:
            bolder title weight (500 → 700), divider rule below the
            block (design audit doc item #3). */}
        <div
          className="np-card-head"
          style={{
            marginBottom: 'var(--s5)',
            paddingBottom: 'var(--s4)',
            borderBottom: '1px solid var(--lines)',
          }}
        >
          <h2 style={{ margin: 0, fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: '1.25rem' }}>
            Project details
          </h2>
          <p className="t-muted t-sm" style={{ marginTop: 4 }}>
            These identify the audit across all three stages. The Audit ID
            is generated automatically once you submit.
          </p>
        </div>

        <form className="np-form" onSubmit={handleSubmit}>
          {/* Sprint 5 client feedback 2026-06-22 — form layout
              rebalanced per design audit doc item #2 ("two-column
              layout"). The Sprint-5 retirements (Investment Type,
              Jurisdiction) left orphaned half-rows in the previous
              layout; this layout pairs every remaining field so the
              grid stays visually balanced. Notes spans full width
              (long-form input — no left/right partner needed). */}

          {/* Row 1: Project name | Audit ID (placeholder) */}
          <div className="form-row">
            <div className="form-group">
              <label className="form-label" htmlFor="projName">
                Project name <span className="req">*</span>
              </label>
              <input
                className="form-input"
                id="projName"
                type="text"
                placeholder="e.g. Project Vega"
                value={form.project_name}
                onChange={(e) => update('project_name', e.target.value)}
                required
              />
            </div>
            <div className="form-group">
              <label className="form-label" htmlFor="auditId">
                Audit ID
              </label>
              {/* Sprint 5 client feedback 2026-07-08 (UI Ticket —
                  New Project font consistency) — route the Audit ID
                  input through the shared .form-input.is-mono utility
                  class instead of inline fontFamily. The shared
                  class + .form-input[readonly] rule set the mono
                  family and muted colour so this input matches the
                  rest of the form typography. */}
              <input
                className="form-input is-mono"
                id="auditId"
                type="text"
                value="AUDIT-YYYY-NNNN (auto-generated)"
                readOnly
              />
            </div>
          </div>

          {/* Sprint 5 client feedback 2026-06-24 (2nd pass) — Target /
              investment entity AND Deal size (OIA equity, $M) BOTH
              removed from project creation, same rationale as the
              Sector removal earlier today: DD analysis extracts
              both from the IC memo / Financial DD reliably and the
              jurisdiction_runner write-back is already wired:
                - target_entity ← target_company_name (already wired)
                - deal_size_usd_m ← deal_size_oia_equity_usd_m
                  (new write-back added below)
              Forcing the analyst to type values at intake produced
              typos that persisted into the report header even after
              DD ran. Empty intake = "discover from documents". */}

          {/* Sprint 5 client feedback 2026-06-24 — Sector input
              removed from project creation. The DD analysis extracts
              the IC memo's "Sector" row (e.g. "Green Hydrogen —
              Electrolyser Industry") via the sub_sector / asset_class
              attributes; jurisdiction_runner auto-writes that back to
              project.sector mid-flight (with an intake_corrections
              audit-log entry). Forcing the analyst to type a sector
              at intake meant a coarse default ("Energy &
              Infrastructure") ended up in the report header even
              when the memo had the specific industry. Row 3 collapses
              to a single Target Close field. */}
          <div className="form-row">
            <div className="form-group">
              <label className="form-label" htmlFor="closeDate">
                Target close
              </label>
              <input
                className="form-input"
                id="closeDate"
                type="text"
                placeholder="e.g. Q4 2026"
                value={form.target_close ?? ''}
                onChange={(e) => update('target_close', e.target.value)}
              />
            </div>
          </div>

          {/* Row 4: Lead analyst (Auto-run Risk toggle removed
              2026-06-23 — the backend hook (auto_trigger.maybe_trigger_risk)
              was scaffolded but never wired into the SPA-complete path,
              so the checkbox had no behavioural effect. Hiding from the
              UI until the trigger is fully wired; backend column
              auto_run_risk stays as-is so the schema doesn't shift.) */}
          <div className="form-row">
            <div className="form-group">
              <label className="form-label" htmlFor="leadAnalyst">
                Lead analyst
              </label>
              <input
                className="form-input"
                id="leadAnalyst"
                type="text"
                value={user?.full_name || user?.email || 'You (signed-in user)'}
                readOnly
                style={{
                  background: 'var(--surface-alt)',
                  color: 'var(--text-sec)',
                }}
              />
            </div>
          </div>

          {/* Row 5: Notes (full width) */}
          <div className="form-group" style={{ marginTop: 'var(--s4)' }}>
            <label className="form-label" htmlFor="notes">
              Notes (optional)
            </label>
            <textarea
              className="form-input"
              id="notes"
              rows={3}
              placeholder="Context, deal thesis, or anything the audit team should know…"
              value={form.notes ?? ''}
              onChange={(e) => update('notes', e.target.value)}
            />
          </div>

          {error && (
            <div className="alert alert-fail" role="alert">
              {error}
            </div>
          )}

          <div className="np-actions">
            <a href="/projects" className="btn btn-secondary">
              Cancel
            </a>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={submitting}
            >
              {submitting ? 'Creating…' : 'Create & Start Due Diligence'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
